// FleetChat.jsx
import { useState, useRef, useEffect, Fragment } from 'react';
import { 
  Sparkles, Copy, FileText, AlertTriangle, Receipt, Car, ArrowUp, Trash2, Clock, CheckCircle,
  XCircle, User, MapPin, Package, CreditCard, Users, FileCheck, Info, ExternalLink, MoreHorizontal, History
} from 'lucide-react';
import { mapBookingToSemanticContext } from './aiSemanticMapper';
import { toast } from 'react-hot-toast';
import { useChatSessions } from './useChatSessions';
import ChatHistorySidebar from './ChatHistorySidebar';
import { FIELD_NAVIGATION_MAP } from './fieldNavigationMap';

function matchesKeyword(text, keyword) {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\b${escaped}\\b`, 'i');
  return pattern.test(text);
}

function matchesAnyKeyword(text, keywords) {
  return keywords.some(k => matchesKeyword(text, k));
}

const LLAMA_ELIGIBLE_KEYWORDS = [
  'status', 'timeline', 'date', 'time', 'window', 'location', 'address', 'hub',
  'customer name', 'customer', 'mobile', 'email', 'package', 'tariff', 'km',
  'agent', 'delivery', 'pickup', 'vehicle', 'model', 'variant', 'registration', 'expire', 'expiry'
];

const GEMINI_ONLY_KEYWORDS = [
  'risk', 'summarize', 'summary', 'analysis', 'analyze', 'unusual', 'issue',
  'problem', 'mismatch', 'flag', 'why', 'compare', 'recommend'
];

function shouldUseLlama(text) {
  if (matchesAnyKeyword(text, GEMINI_ONLY_KEYWORDS)) return false;
  return matchesAnyKeyword(text, LLAMA_ELIGIBLE_KEYWORDS);
}

async function callLlama(compressedContext, query) {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/aiserverless/ai-booking-analysis`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': token 
      },
      body: JSON.stringify({ bookingData: compressedContext, query })
    });
    if (!response.ok) return null;
    const data = await response.json();
    const reply = data?.dataValues || data?.reply || data?.response || data?.text || data?.message || data?.output || null;
    if (!reply || reply.trim().length === 0) return null;
    return reply;
  } catch (error) {
    console.error('Llama call failed:', error);
    return null;
  }
}



function buildConversationContext(bookings, queryIntent = '') {
  if (!bookings) return { context: 'No booking data available.', imageUrls: [], imageTags: [], customerDocUrls: [], customerDocNames: [], customerDocStatuses: [], customerDocExpiries: [], customerDocIds: [], vehicleDocUrls: [], vehicleDocNames: [], vehicleDocStatuses: [], vehicleDocExpiries: [], vehicleDocIds: [] };
  const data = Array.isArray(bookings) ? bookings[0] : bookings;
  if (!data) return { context: 'No valid booking data available.', imageUrls: [], imageTags: [], customerDocUrls: [], customerDocNames: [], customerDocStatuses: [], customerDocExpiries: [], customerDocIds: [], vehicleDocUrls: [], vehicleDocNames: [], vehicleDocStatuses: [], vehicleDocExpiries: [], vehicleDocIds: [] };
  return mapBookingToSemanticContext(data, queryIntent);
}

const QUICK_ACTIONS = [
  { label: 'Summary', icon: FileText, question: 'Summarize this booking completely' },
  { label: 'Risks', icon: AlertTriangle, question: 'What are the risks or issues with this booking?' },
  { label: 'Customer', icon: User, question: 'Tell me about the customer details' },
  { label: 'Documents', icon: FileCheck, question: 'Are all customer documents verified?' },
  { label: 'Pricing', icon: Receipt, question: 'Show me the complete pricing breakdown' },
  { label: 'Payment', icon: CreditCard, question: 'What is the payment status and transaction details?' },
  { label: 'Vehicle', icon: Car, question: 'Tell me about the vehicle specifications' },
  { label: 'Package', icon: Package, question: 'What are the package details and free kilometers?' },
  { label: 'Location', icon: MapPin, question: 'Details - Address?' },
  { label: 'Agents', icon: Users, question: 'Are agents assigned for delivery and pickup?' },
  { label: 'Timeline', icon: Clock, question: 'What is the booking timeline and duration?' },
  { label: 'Status', icon: Info, question: 'What is the current booking status?' },
];

function filterImagesByQuery(query, imageTags, imageUrls) {
  if (!imageTags || !imageUrls || imageUrls.length === 0) return { urls: [], tags: [] };
  const stopWords = ['i', 'want', 'to', 'see', 'view', 'show', 'me', 'the', 'images', 'image', 'photos', 'photo', 'pictures', 'picture', 'only', 'just', 'all', 'more', 'every', 'pls', 'please'];
  const keywords = query.toLowerCase().split(' ').filter(k => k.length > 2 && !stopWords.includes(k));
  if (keywords.length === 0) return { urls: imageUrls, tags: imageTags };
  
  const filtered = [];
  for (let i = 0; i < imageTags.length; i++) {
    const tag = imageTags[i].toLowerCase();
    const matches = keywords.some(keyword => {
      const keywordSingular = keyword.replace(/s$/, '');
      const tagSingular = tag.replace(/s$/, '');
      return tag.includes(keyword) || keyword.includes(tag) || tagSingular.includes(keywordSingular) || keywordSingular.includes(tagSingular);
    });
    if (matches) filtered.push({ url: imageUrls[i], tag: imageTags[i] });
  }
  if (filtered.length === 0) return { urls: imageUrls, tags: imageTags };
  return { urls: filtered.map(f => f.url), tags: filtered.map(f => f.tag) };
}

export default function FleetChat({ bookings, geminiApiKey, onNavigateToTarget }) {
  const bookingData = Array.isArray(bookings) ? bookings[0] : bookings;
  
  // Custom Session Hook
  const {
    sessions, activeSessionId, setActiveSessionId,
    createSession, updateSessionMessages, deleteSession, bulkAddSessions
  } = useChatSessions();

  // One-time Migration from old localStorage format
  useEffect(() => {
    if (localStorage.getItem('fleet_chat_migrated') === 'true') return;

    const migratedSessions = [];
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('fleet_chat_history_')) {
        const bCode = key.replace('fleet_chat_history_', '');
        try {
          const rawMessages = localStorage.getItem(key);
          const parsedMsgs = JSON.parse(rawMessages);
          
          if (Array.isArray(parsedMsgs) && parsedMsgs.length > 0) {
            migratedSessions.push({
              id: `session_migrated_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
              title: `Booking ${bCode}`,
              bookingCode: bCode,
              updatedAt: Date.now(),
              messages: parsedMsgs
            });
          }
        } catch (e) {
          console.error('Failed to parse old chat history for migration', e);
        }
        keysToRemove.push(key);
      }
    }

    if (migratedSessions.length > 0 && bulkAddSessions) {
      bulkAddSessions(migratedSessions);
    }

    keysToRemove.forEach(k => localStorage.removeItem(k));
    localStorage.setItem('fleet_chat_migrated', 'true');
  }, [bulkAddSessions]);


  // Synchronous Initialization to avoid the empty flash
  const [messages, setMessages] = useState(() => {
    if (!bookingData?.bookingCode) return [];
    
    const existingSession = sessions.find(s => s.bookingCode === bookingData.bookingCode);
    if (existingSession && existingSession.messages?.length > 0) {
      return existingSession.messages;
    }
    
    return [{ 
      role: 'assistant', 
      text: `👋 Hi! I'm analyzing booking ${bookingData?.bookingCode || 'booking'} for ${bookingData?.customerName || 'Customer'}.\n\nAsk me about pricing, risks, documents, or any detail.` 
    }];
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Fallback when active session is deleted or invalid
  useEffect(() => {
    if (!bookingData?.bookingCode) return;
    
    const activeExists = sessions.some(s => s.id === activeSessionId);
    
    if (!activeExists) {
      const remainingForBooking = sessions
        .filter(s => s.bookingCode === bookingData.bookingCode)
        .sort((a, b) => b.updatedAt - a.updatedAt);
        
      if (remainingForBooking.length > 0) {
        setActiveSessionId(remainingForBooking[0].id);
      } else {
        createSession(bookingData.bookingCode);
      }
    }
  }, [sessions, activeSessionId, bookingData?.bookingCode, setActiveSessionId, createSession]);

  useEffect(() => {
    if (!activeSessionId) return;
    
    const session = sessions.find(s => s.id === activeSessionId);
    if (session) {
      if (session.messages.length > 0) {
        setMessages(session.messages);
      } else {
        setMessages([{ 
          role: 'assistant', 
          text: `👋 Hi! I'm analyzing booking ${bookingData?.bookingCode || 'booking'} for ${bookingData?.customerName || 'Customer'}.\n\nAsk me about pricing, risks, documents, or any detail.` 
        }]);
      }
    }
  }, [activeSessionId]);

  useEffect(() => {
    if (activeSessionId && messages.length > 0) {
      updateSessionMessages(activeSessionId, messages);
    }
  }, [messages, activeSessionId, updateSessionMessages]);


  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentName, setSelectedDocumentName] = useState('');
  
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentGalleryImages, setCurrentGalleryImages] = useState([]);

  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;
  const scrollRef = useRef(null);
  const dropdownRef = useRef(null);
  const [mediaError, setMediaError] = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState(true);

  useEffect(() => { 
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; 
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e) => { 
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target) &&
        !e.target.closest('#more-actions-btn')
      ) {
        setDropdownOpen(false); 
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getSmartSuggestions = () => {
    if (!bookingData) return [];
    const suggestions = [];
    
    if (bookingData.pendingAmount > 0) suggestions.push({ text: `Pending: ₹${bookingData.pendingAmount}`, alert: true });
    if (bookingData.totalAmountPaid === 0 && bookingData.costToCustomer > 0) suggestions.push({ text: 'No payment received', alert: true });
    
    const statusDetails = (bookingData.bookingStatusDetails || '').toLowerCase();
    const status = String(bookingData.bookingStatus || '');
    const isTripCompleted = statusDetails.includes('trip completed') || status === 'tripCompleted';

    const fareBreakup = bookingData._fareBreakup || bookingData.fareBreakUp || [];
    const totalAdditionalCharges = fareBreakup.reduce((sum, item) => sum + Number(item.amount || item.price || 0), 0) || Number(bookingData.pendingAmount || 0);
    const deposit = Number(bookingData.securityDeposit || 0);

    if (isTripCompleted && deposit > 0 && totalAdditionalCharges > deposit) {
      suggestions.push({ text: 'Recovery Risk: Charges Exceed Deposit', alert: true, color: 'red' });
    }

    const docs = bookingData._customerDocuments || bookingData.customerDocuments || [];
    const hasDocumentsUploaded = docs.length > 0 && docs.some(d => d.url || d.documentUrl || d.fileUrl || d.isUploaded);

    const isVerificationPending = (statusDetails.includes('verification pending') || status === 'verificationPending') || hasDocumentsUploaded;
    
    if (isVerificationPending) {
      suggestions.push({ text: 'Verification Pending', alert: true });
    }

    if (!hasDocumentsUploaded && (statusDetails.includes('document pending') || status === 'documentPending' || docs.length === 0)) {
      suggestions.push({ text: 'Document Pending', alert: true });
    }

    const isEnroute = statusDetails.includes('enroute') || status === 'enroute';
    const hasPickupAgents = (bookingData._pickupAgents?.length > 0) || bookingData.isPickupAgentAssigned;
    if (isEnroute && !hasPickupAgents) {
      suggestions.push({ text: 'Risk: Pickup Agent Not Assigned', alert: true, color: 'red' });
    }

    const isBookingConfirmed = statusDetails.includes('booking confirmed') || status === 'bookingConfirmed';
    const hasDeliveryAgents = (bookingData._deliveryAgents?.length > 0) || bookingData.isDeliveryAgentAssigned;
    if (isBookingConfirmed && !hasDeliveryAgents) {
      suggestions.push({ text: 'Risk: Delivery Agent Not Assigned', alert: true, color: 'red' });
    }

    if (!isEnroute && !isBookingConfirmed && !hasDeliveryAgents && !hasPickupAgents) {
      suggestions.push({ text: 'No delivery agent', alert: true });
    }

    if (bookingData.cancellationReason) suggestions.push({ text: 'Cancelled booking', alert: true });
    
    return suggestions.slice(0, 3);
  };

  const smartSuggestions = getSmartSuggestions();

  async function sendMessage(overrideText) {
    const text = overrideText ?? input;
    if (!text.trim() || !bookings) return;
    setMessages(prev => [...prev, { role: 'user', text: text.trim() }]);
    setInput(''); 
    setDropdownOpen(false); 
    setLoading(true);
    retryCountRef.current = 0;

    try {
      const result = buildConversationContext(bookings, text);
      
      if (result.context.startsWith('ERROR') || result.context.startsWith('No')) {
        throw new Error(result.context);
      }

      const allImageUrls = result.imageUrls || [];
      const allImageTags = result.imageTags || [];
      const customerDocUrls = result.customerDocUrls || [];
      const customerDocNames = result.customerDocNames || [];
      const customerDocStatuses = result.customerDocStatuses || [];
      const customerDocExpiries = result.customerDocExpiries || [];
      const vehicleDocUrls = result.vehicleDocUrls || [];
      const vehicleDocNames = result.vehicleDocNames || [];
      const vehicleDocStatuses = result.vehicleDocStatuses || [];
      const vehicleDocExpiries = result.vehicleDocExpiries || [];

      const allDocUrls = [...customerDocUrls, ...vehicleDocUrls];
      const allDocNames = [...customerDocNames, ...vehicleDocNames];
      const allDocStatuses = [...customerDocStatuses, ...vehicleDocStatuses];
      const allDocExpiries = [...customerDocExpiries, ...vehicleDocExpiries];

      const validImageUrls = allImageUrls.filter(url => url && url.trim() !== '').map(url => {
          if (url.startsWith('/')) return url;
          if (!url.startsWith('http://') && !url.startsWith('https://')) return `https://${url}`;
          return url;
      });

      const validDocUrls = allDocUrls.filter(url => url && url.trim() !== '').map(url => {
          if (!url.startsWith('http://') && !url.startsWith('https://')) return `https://${url}`;
          return url;
      });

      const textLower = text.toLowerCase();
      const imageKeywords = ['image', 'photo', 'picture', 'pic', 'images', 'photos', 'pictures', 'pics'];
      const imagePhrases = ['show images', 'show me images', 'see images', 'view images', 'display images', 'images?'];
      const isImageQuery = matchesAnyKeyword(text, imageKeywords) || imagePhrases.some(p => textLower.includes(p));

      const documentKeywords = ['document', 'documents', 'doc', 'docs', 'papers', 'file', 'files', 'form', 'rc', 'insurance', 'puc', 'roadtax', 'expire', 'expiry', 'valid', 'verification', 'verify', 'verified'];
      const documentPhrases = ['vehicle document', 'customer document', 'show docs', 'view docs', 'vehicle docs', 'customer docs'];
      const isDocumentQuery = matchesAnyKeyword(text, documentKeywords) || documentPhrases.some(p => textLower.includes(p));

      const prevMessageHadImages = messages.length > 0 && messages[messages.length - 1]?.imageUrls?.length > 0;
      const prevMessageHadDocs = messages.length > 0 && (messages[messages.length - 1]?.docNames?.length > 0 || messages[messages.length - 1]?.customerDocNames?.length > 0 || messages[messages.length - 1]?.vehicleDocNames?.length > 0);
      const isReferencingImages = matchesKeyword(text, 'them') && prevMessageHadImages;
      const isReferencingDocs = matchesKeyword(text, 'them') && prevMessageHadDocs;

      const shouldShowImages = (isImageQuery || isReferencingImages) && validImageUrls.length > 0;
      const shouldShowDocuments = (isDocumentQuery || isReferencingDocs) && (customerDocNames.length > 0 || vehicleDocNames.length > 0);

      const filteredImages = filterImagesByQuery(text, allImageTags, validImageUrls);
      const wantsMoreImages = matchesKeyword(text, 'more') || matchesKeyword(text, 'all') || matchesKeyword(text, 'every') || text.toLowerCase().includes('show me more');
      const MAX_IMAGES_DEFAULT = 10;
      const MAX_IMAGES_MORE = 100;
      const maxImages = wantsMoreImages ? MAX_IMAGES_MORE : MAX_IMAGES_DEFAULT;

      const pairedImages = filteredImages.urls.map((url, index) => ({
        url: url,
        tag: filteredImages.tags[index] || `Image ${index + 1}`
      }));

      const displayImages = pairedImages.slice(0, maxImages);
      const displayUrls = displayImages.map(img => img.url);
      const displayTags = displayImages.map(img => img.tag);
      const totalMatchingImages = pairedImages.length;

      const wantsCustomerDocs = matchesKeyword(text, 'customer') && (matchesKeyword(text, 'doc') || matchesKeyword(text, 'document'));
      const wantsVehicleDocs = matchesKeyword(text, 'vehicle') && (matchesKeyword(text, 'doc') || matchesKeyword(text, 'document'));

      let displayDocNames = [], displayDocStatuses = [], displayDocExpiries = [], totalMatchingDocs = 0;

      if (shouldShowDocuments) {
        if (wantsCustomerDocs && customerDocNames.length > 0) {
          displayDocNames = customerDocNames; displayDocStatuses = customerDocStatuses; displayDocExpiries = customerDocExpiries; totalMatchingDocs = customerDocNames.length;
        } else if (wantsVehicleDocs && vehicleDocNames.length > 0) {
          displayDocNames = vehicleDocNames; displayDocStatuses = vehicleDocStatuses; displayDocExpiries = vehicleDocExpiries; totalMatchingDocs = vehicleDocNames.length;
        } else {
          displayDocNames = allDocNames; displayDocStatuses = allDocStatuses; displayDocExpiries = allDocExpiries; totalMatchingDocs = allDocNames.length;
        }
      }

      const reasoningKeywords = ['why', 'risk', 'issue', 'problem', 'summarize', 'analysis', 'analyze', 'flag', 'concern', 'expired', 'valid', 'compare'];
      const hasReasoningIntent = matchesAnyKeyword(textLower, reasoningKeywords);
      const otherDomainKeywords = ['agent', 'delivery', 'pickup', 'fare', 'price', 'cost', 'package', 'status', 'customer', 'vehicle', 'timeline', 'extension', 'reschedule', 'cancel', 'ticket'];
      const hasOtherDomainIntent = matchesAnyKeyword(textLower, otherDomainKeywords);
      
      const isPureDisplayQuery = (isImageQuery || isDocumentQuery || isReferencingImages || isReferencingDocs) && !hasReasoningIntent && !hasOtherDomainIntent;

      if (isPureDisplayQuery && (shouldShowImages || shouldShowDocuments)) {
        const localReply = shouldShowImages && shouldShowDocuments
          ? `Here are the images and documents for this booking.`
          : shouldShowImages
            ? `Here are the images for this booking (${totalMatchingImages} found).`
            : `Here are the documents for this booking (${totalMatchingDocs} found).`;
        
        setMessages(m => [...m, {
          role: 'assistant', text: localReply,
          imageUrls: displayUrls.length > 0 ? displayUrls : undefined,
          imageTags: displayTags.length > 0 ? displayTags : undefined,
          totalMatchingImages: totalMatchingImages || validImageUrls.length || 0,
          docNames: displayDocNames.length > 0 ? displayDocNames : undefined,
          docStatuses: displayDocStatuses.length > 0 ? displayDocStatuses : undefined,
          docExpiries: displayDocExpiries.length > 0 ? displayDocExpiries : undefined,
          docUrls: validDocUrls.length > 0 ? validDocUrls : undefined,
          customerDocNames: (!wantsVehicleDocs && customerDocNames.length > 0) ? customerDocNames : undefined,
          customerDocStatuses: (!wantsVehicleDocs && customerDocStatuses.length > 0) ? customerDocStatuses : undefined,
          customerDocExpiries: (!wantsVehicleDocs && customerDocExpiries.length > 0) ? customerDocExpiries : undefined,
          customerDocUrls: (!wantsVehicleDocs && customerDocUrls.length > 0) ? customerDocUrls : undefined,
          vehicleDocNames: (!wantsCustomerDocs && vehicleDocNames.length > 0) ? vehicleDocNames : undefined,
          vehicleDocStatuses: (!wantsCustomerDocs && vehicleDocStatuses.length > 0) ? vehicleDocStatuses : undefined,
          vehicleDocExpiries: (!wantsCustomerDocs && vehicleDocExpiries.length > 0) ? vehicleDocExpiries : undefined,
          vehicleDocUrls: (!wantsCustomerDocs && vehicleDocUrls.length > 0) ? vehicleDocUrls : undefined,
          totalMatchingDocs: totalMatchingDocs || allDocNames.length || 0
        }]);
        setLoading(false);
        retryCountRef.current = 0;
        return;
      }

      let finalContext = result.context;
      const historyStr = messages.slice(-4).map(m => `${m.role}: ${m.text}`).join('\n') + `\nuser: ${text}`;
      const totalMatching = filteredImages.urls.length || validImageUrls.length;
      const shownCount = displayUrls.length;
      const imageInfoText = totalMatching > 0 
        ? `Found ${totalMatching} images${shownCount < totalMatching ? `, showing ${shownCount} (say "show more" to see all)` : ''}.`
        : 'No images found.';

      const docDetailStr = (isDocumentQuery || isReferencingDocs) && allDocNames.length > 0
        ? `\n\nDOCUMENTS_INFO:\n` + allDocNames.map((name, i) => `- ${name}: Status=${allDocStatuses[i] || 'Unverified'}, Expiry=${allDocExpiries[i] || 'N/A'}`).join('\n')
        : '';

      const prompt = `You are an expert internal fleet support assistant. Context summary of booking:\n${finalContext}${docDetailStr}\n\n${imageInfoText}\n\nRecent conv:\n${historyStr}\n\nAnswer under 60 words using this data. Be specific with numbers. Do not mention documents or expiration dates unless specifically asked by the user.`;

      let reply = null;
      if (shouldUseLlama(text)) {
        reply = await callLlama(finalContext, text);
      }

      if (!reply) {
        const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite:generateContent?key=${geminiApiKey}`, {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
        });

        if (res.status === 429) {
          if (retryCountRef.current >= MAX_RETRIES) throw new Error('Rate limit exceeded. Please try again later.');
          const delay = (await res.json())?.error?.message?.match(/retry in (\d+\.?\d*)s/)?.[1] * 1000 || 30000;
          setMessages(m => [...m, { role: 'assistant', text: `⏳ Rate limited — retrying in ${Math.ceil(delay / 1000)}s (attempt ${retryCountRef.current + 1}/${MAX_RETRIES}).` }]);
          retryCountRef.current += 1;
          setTimeout(() => { setMessages(m => m.slice(0, -1)); sendMessage(text); }, delay);
          setLoading(false);
          return;
        }

        const data = await res.json();
        reply = data.candidates?.[0]?.content?.parts?.[0]?.text || data.candidates?.[0]?.text || data.error?.message || 'Error parsing response';
      }

      const matchedSources = [];
      const seenTargets = new Set();
      for (const [keyword, sourceData] of Object.entries(FIELD_NAVIGATION_MAP)) {
        if ((text + " " + reply).toLowerCase().includes(keyword)) {
          const key = `${sourceData.tab?.tabId}-${sourceData.accordion?.accordionId}`;
          if (!seenTargets.has(key)) {
            seenTargets.add(key);
            matchedSources.push(sourceData);
            if (matchedSources.length >= 2) break;
          }
        }
      }
      
      setMessages(m => [...m, { 
        role: 'assistant', text: reply, 
        sources: matchedSources.length > 0 ? matchedSources : undefined,
        imageUrls: shouldShowImages ? displayUrls : undefined,
        imageTags: shouldShowImages ? displayTags : undefined,
        totalMatchingImages: shouldShowImages ? (totalMatchingImages || validImageUrls.length || 0) : undefined,
        docNames: displayDocNames.length > 0 ? displayDocNames : undefined,
        docStatuses: displayDocStatuses.length > 0 ? displayDocStatuses : undefined,
        docExpiries: displayDocExpiries.length > 0 ? displayDocExpiries : undefined,
        docUrls: shouldShowDocuments ? validDocUrls : undefined,
        customerDocNames: (!wantsVehicleDocs && customerDocNames.length > 0) ? customerDocNames : undefined,
        customerDocStatuses: (!wantsVehicleDocs && customerDocStatuses.length > 0) ? customerDocStatuses : undefined,
        customerDocExpiries: (!wantsVehicleDocs && customerDocExpiries.length > 0) ? customerDocExpiries : undefined,
        customerDocUrls: shouldShowDocuments ? customerDocUrls : undefined,
        vehicleDocNames: (!wantsCustomerDocs && vehicleDocNames.length > 0) ? vehicleDocNames : undefined,
        vehicleDocStatuses: (!wantsCustomerDocs && vehicleDocStatuses.length > 0) ? vehicleDocStatuses : undefined,
        vehicleDocExpiries: (!wantsCustomerDocs && vehicleDocExpiries.length > 0) ? vehicleDocExpiries : undefined,
        vehicleDocUrls: shouldShowDocuments ? vehicleDocUrls : undefined,
        totalMatchingDocs: shouldShowDocuments ? (totalMatchingDocs || allDocNames.length || 0) : undefined
      }]);
      
      retryCountRef.current = 0;
      
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', text: `❌ ${err.message || 'Request failed.'}` }]);
      retryCountRef.current = 0;
    } finally {
      setLoading(false);
    }
  }

  const parseMarkdown = (text) => {
    return text.split('\n').map((line, i) => (
      <Fragment key={i}>
        {line.split(/(\*\*.*?\*\*)/g).map((part, j) => 
          part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong> : part
        )}
        {i < text.split('\n').length - 1 && <br />}
      </Fragment>
    ));
  };
  
  return (
    <div className="relative flex h-[95%] w-full flex-col min-h-0 min-w-0 overflow-hidden p-4 bg-slate-50/50 rounded-xl border border-slate-200 shadow-sm font-sans">
      
      {/* Sidebar Overlay */}
      <ChatHistorySidebar 
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSelectSession={setActiveSessionId}
        onDeleteSession={deleteSession}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Enhanced Image Viewer Modal */}
      {isImageViewerOpen && (selectedImage || currentGalleryImages.length > 0) && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => { setIsImageViewerOpen(false); setSelectedImage(null); setCurrentGalleryImages([]); setMediaError(false); setIsMediaLoading(true); }}>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-5xl max-h-[90vh] p-4 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-full flex justify-between items-center mb-2 px-2">
              <span className="text-xs font-semibold text-slate-700">
                {currentGalleryImages[selectedImageIndex]?.tag || 'Booking Media'} ({selectedImageIndex + 1} of {currentGalleryImages.length})
              </span>
              <button onClick={() => { setIsImageViewerOpen(false); setSelectedImage(null); setCurrentGalleryImages([]); setMediaError(false); setIsMediaLoading(true); }} className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-all focus:outline-none">
                <XCircle size={20} />
              </button>
            </div>
            <div className="relative flex items-center justify-center min-h-[300px] min-w-[300px] max-w-[80vw] max-h-[75vh]">
              {currentGalleryImages.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); setIsMediaLoading(true); setMediaError(false); setSelectedImageIndex((prev) => (prev === 0 ? currentGalleryImages.length - 1 : prev - 1)); }} className="absolute left-2 z-30 bg-black/60 hover:bg-black/80 text-white rounded-full p-2.5 transition-all focus:outline-none shadow-md">◀</button>
              )}
              {isMediaLoading && !mediaError && <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-100/50 rounded-lg"><div className="w-10 h-10 border-4 border-sky-600 border-t-transparent rounded-full animate-spin" /></div>}
              {(() => {
                const activeMediaUrl = currentGalleryImages[selectedImageIndex]?.url || selectedImage;
                const isVideo = activeMediaUrl && (activeMediaUrl.endsWith('.mp4') || activeMediaUrl.endsWith('.webm') || activeMediaUrl.endsWith('.mov') || activeMediaUrl.endsWith('.avi'));
                if (isVideo) {
                  return <video key={activeMediaUrl} controls autoPlay className="max-w-full max-h-[70vh] rounded-lg object-contain" onLoadStart={() => { setIsMediaLoading(true); setMediaError(false); }} onLoadedData={() => { setIsMediaLoading(false); setMediaError(false); }} onError={() => { setIsMediaLoading(false); setMediaError(true); }}><source src={activeMediaUrl} type="video/mp4" />Your browser does not support the video tag.</video>;
                } else {
                  return <img key={activeMediaUrl} src={activeMediaUrl} alt="Booking media viewer" className="max-w-full max-h-[70vh] object-contain rounded-lg" onLoadStart={() => { setIsMediaLoading(true); setMediaError(false); }} onLoad={() => { setIsMediaLoading(false); setMediaError(false); }} onError={() => { setIsMediaLoading(false); setMediaError(true); }} />;
                }
              })()}
              {currentGalleryImages.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); setIsMediaLoading(true); setMediaError(false); setSelectedImageIndex((prev) => (prev === currentGalleryImages.length - 1 ? 0 : prev + 1)); }} className="absolute right-2 z-30 bg-black/60 hover:bg-black/80 text-white rounded-full p-2.5 transition-all focus:outline-none shadow-md">▶</button>
              )}
              {mediaError && <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm bg-black/70 rounded-lg p-4 z-20"><AlertTriangle size={48} className="mb-2 text-rose-500" /><p>Failed to load media file</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && selectedDocument && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => { setIsDocumentViewerOpen(false); setSelectedDocument(null); setSelectedDocumentName(''); setMediaError(false); setIsMediaLoading(true); }}>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl max-h-[90vh] p-2" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setIsDocumentViewerOpen(false); setSelectedDocument(null); setSelectedDocumentName(''); setMediaError(false); setIsMediaLoading(true); }} className="absolute top-3 right-3 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-white">
              <XCircle size={24} />
            </button>
            <div className="relative flex items-center justify-center min-h-[200px] min-w-[200px]">
              {isMediaLoading && !mediaError && <div className="absolute inset-0 flex items-center justify-center z-10"><div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>}
              {(() => {
                const isImage = selectedDocument && (selectedDocument.endsWith('.jpg') || selectedDocument.endsWith('.jpeg') || selectedDocument.endsWith('.png') || selectedDocument.endsWith('.gif') || selectedDocument.endsWith('.webp'));
                const isVideo = selectedDocument && (selectedDocument.endsWith('.mp4') || selectedDocument.endsWith('.webm') || selectedDocument.endsWith('.mov') || selectedDocument.endsWith('.avi'));
                const isPDF = selectedDocument && selectedDocument.endsWith('.pdf');
                if (isPDF) {
                  return <iframe src={`${selectedDocument}#toolbar=1`} className="w-full h-[80vh] rounded-lg" title={selectedDocumentName || 'Document'} onLoad={() => { setIsMediaLoading(false); setMediaError(false); }} onError={() => { setIsMediaLoading(false); setMediaError(true); }} />;
                } else if (isVideo) {
                  return <video key={selectedDocument} controls autoPlay className="max-w-full max-h-[80vh] rounded-lg" onLoadStart={() => { setIsMediaLoading(true); setMediaError(false); }} onLoadedData={() => { setIsMediaLoading(false); setMediaError(false); }} onError={() => { setIsMediaLoading(false); setMediaError(true); }}><source src={selectedDocument} type="video/mp4" /></video>;
                } else if (isImage) {
                  return <img src={selectedDocument} alt={selectedDocumentName || 'Document'} className="max-w-full max-h-[80vh] object-contain rounded-lg" onLoadStart={() => { setIsMediaLoading(true); setMediaError(false); }} onLoad={() => { setIsMediaLoading(false); setMediaError(false); }} onError={() => { setIsMediaLoading(false); setMediaError(true); }} />;
                } else {
                  return <div className="flex flex-col items-center justify-center p-8 text-center"><FileText size={64} className="text-slate-400 mb-4" /><p className="text-slate-600 font-medium">{selectedDocumentName || 'Document'}</p><a href={selectedDocument} target="_blank" rel="noopener noreferrer" className="mt-4 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors">Open in New Tab</a></div>;
                }
              })()}
              {mediaError && <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm bg-black/70 rounded-lg p-4 z-20"><AlertTriangle size={48} className="mb-2 text-rose-500" /><p>Failed to load document</p><a href={selectedDocument} target="_blank" rel="noopener noreferrer" className="mt-3 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm">Open in New Tab</a></div>}
            </div>
          </div>
        </div>
      )}

      {/* Header Area */}
      <header className="p-4 bg-white border-b border-slate-200 shadow-sm z-10 w-full flex flex-col gap-3 shrink-0">
        <div className="flex justify-between items-start w-full gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
              <div className="w-2 h-2 rounded-full bg-emerald-500 motion-safe:animate-pulse ring-2 ring-emerald-500/20" />
              <h2 className="text-sm font-semibold text-slate-800 tracking-tight">{bookingData?.bookingCode || 'New Booking'}</h2>
            </div>
            {(bookingData?.bookingStatusDetails || '').toLowerCase() && (
              <span className="text-[11px] px-2 py-0.5 rounded-md font-semibold uppercase tracking-wider border bg-slate-100 text-slate-800 border-slate-200">
                {bookingData.bookingStatusDetails}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setIsSidebarOpen(true)} 
              className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-md hover:bg-indigo-50 focus:outline-none transition-colors" 
              title="View Chat History"
            >
              <History size={16} />
            </button>
            <div className="w-px h-4 bg-slate-300 mx-1"></div>
            <button 
              onClick={() => { 
                const defaultMsg = [{ role: 'assistant', text: `👋 Hi! I'm analyzing booking ${bookingData?.bookingCode || 'booking'} for ${bookingData?.customerName || 'Customer'}.\n\nAsk me about pricing, risks, documents, or any detail.` }];
                setMessages(defaultMsg);
                if (activeSessionId) updateSessionMessages(activeSessionId, defaultMsg);
              }} 
              className="p-1.5 text-slate-400 hover:text-rose-600 rounded-md hover:bg-rose-50 focus:outline-none transition-colors" 
              disabled={messages.length <= 1}
              title="Clear current chat"
            >
              <Trash2 size={16} />
            </button>
            <button 
              onClick={async () => { await navigator.clipboard.writeText(messages.map(m => `${m.role}: ${m.text}`).join('\n\n')); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); }} 
              className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 focus:outline-none transition-colors" 
              disabled={messages.length === 0}
              title="Copy chat"
            >
              {copySuccess ? <CheckCircle size={16} className="text-emerald-500" /> : <Copy size={16} />}
            </button>
          </div>
        </div>
      </header>

      {/* Messages Area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-5 w-full" ref={scrollRef} role="log">
        {messages.map((m, i) => (
          <div key={i} className={`flex w-full gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role !== 'user' && (
              <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles size={14} className="text-sky-600" />
              </div>
            )}
            <div className={`flex flex-col gap-1.5 max-w-[85%] min-w-0 ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
              <div className={`text-[14px] leading-relaxed px-4 py-3 break-words shadow-sm min-w-0 w-fit ${m.role === 'user' ? 'bg-sky-600 text-white rounded-2xl rounded-tr-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-2xl rounded-tl-sm'}`}>
                {m.role === 'user' ? m.text : parseMarkdown(m.text)}
              </div>
              
              {/* Image Links with Gallery Support */}
              {m.role !== 'user' && m.imageUrls && m.imageUrls.length > 0 && (
                <div className="flex flex-wrap gap-2 px-1 mt-2">
                  {m.imageUrls.map((url, idx) => {
                    const isValidUrl = url && url.trim() !== '' && (url.startsWith('http://') || url.startsWith('https://'));
                    const isVideo = url && (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi'));
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          if (isValidUrl) {
                            const messageGallery = m.imageUrls.map((u, i) => ({ url: u, tag: m.imageTags?.[i] || `Media ${i + 1}` }));
                            setCurrentGalleryImages(messageGallery);
                            setSelectedImageIndex(idx);
                            setSelectedImage(url);
                            setIsImageViewerOpen(true);
                            setIsMediaLoading(true);
                            setMediaError(false);
                          } else {
                            toast.error('This media URL is invalid or unavailable');
                          }
                        }}
                        className="text-[11px] font-medium text-sky-600 hover:text-sky-800 border border-sky-200 hover:border-sky-300 bg-sky-50 hover:bg-sky-100 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 transition-all max-w-[200px] truncate cursor-pointer"
                        title={url}
                      >
                        {isVideo ? <span className="text-purple-500 flex-shrink-0">▶️</span> : <ExternalLink size={12} className="text-sky-500 flex-shrink-0" />}
                        {m.imageTags && m.imageTags[idx] ? m.imageTags[idx] : `Media ${idx + 1}`}
                      </button>
                    );
                  })}
                  
                  {m.totalMatchingImages && m.totalMatchingImages > m.imageUrls.length && (
                    <button
                      onClick={() => {
                        const tagHint = m.imageTags && m.imageTags.length > 0 ? m.imageTags[0].split(' ')[0] : 'images';
                        sendMessage(`show all ${tagHint} images`);
                      }}
                      className="text-[11px] font-medium text-slate-500 hover:text-sky-600 border border-slate-300 hover:border-sky-300 bg-white hover:bg-sky-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <span>Show more</span>
                      <span className="text-[10px] opacity-60">({m.totalMatchingImages - m.imageUrls.length} more)</span>
                    </button>
                  )}
                </div>
              )}

              {/* Isolated Document Links */}
              {m.role !== 'user' && (m.customerDocNames?.length > 0 || m.vehicleDocNames?.length > 0) && (
                <div className="flex flex-col gap-2.5 mt-2.5 w-full">
                  {m.customerDocNames && m.customerDocNames.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase px-1">👤 Customer Documents</span>
                      <div className="flex flex-wrap gap-2 px-1">
                        {m.customerDocNames.map((name, idx) => {
                          let docUrl = m.customerDocUrls?.[idx] || '';
                          let docStatus = m.customerDocStatuses?.[idx] || '';
                          let urls = [];
                          if (docUrl) {
                            if (typeof docUrl === 'string' && docUrl.includes(',')) urls = docUrl.split(',').map(u => u.trim()).filter(u => u);
                            else if (Array.isArray(docUrl)) urls = docUrl;
                            else urls = [docUrl];
                          }
                          const hasUrl = urls.length > 0;
                          return (
                            <button
                              key={`cust-doc-${idx}`}
                              onClick={() => {
                                if (hasUrl) { setSelectedDocument(urls[0]); setSelectedDocumentName(name); setIsDocumentViewerOpen(true); setIsMediaLoading(true); setMediaError(false); } 
                                else { toast.error('No file URL available for this document'); }
                              }}
                              className={`text-[11px] font-medium border rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 transition-all max-w-full truncate cursor-pointer ${hasUrl ? 'text-sky-600 hover:text-sky-800 border-sky-200 hover:border-sky-300 bg-sky-50 hover:bg-sky-100' : 'text-slate-400 border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'}`}
                              disabled={!hasUrl}
                            >
                              <FileText size={12} className={hasUrl ? 'text-sky-500' : 'text-slate-400'} />
                              <span className="truncate max-w-[150px]">{name}</span>
                              {docStatus && docStatus.toLowerCase() !== 'pending' && (
                                <span className={`text-[8px] font-bold ml-1 px-1.5 py-0.5 rounded ${docStatus.toLowerCase() === 'verified' ? 'bg-green-100 text-green-700' : docStatus.toLowerCase() === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{docStatus}</span>
                              )}
                              {hasUrl && urls.length > 1 && <span className="text-[8px] text-sky-400 font-bold ml-0.5">+{urls.length - 1}</span>}
                              {hasUrl && <ExternalLink size={10} className="text-sky-400 flex-shrink-0 ml-1" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {m.vehicleDocNames && m.vehicleDocNames.length > 0 && (
                    <div className="flex flex-col gap-1 mt-1">
                      <span className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase px-1">🚗 Vehicle Compliance Docs</span>
                      <div className="flex flex-wrap gap-2 px-1">
                        {m.vehicleDocNames.map((name, idx) => {
                          let docUrl = m.vehicleDocUrls?.[idx] || '';
                          let docStatus = m.vehicleDocStatuses?.[idx] || '';
                          let urls = [];
                          if (docUrl) {
                            if (typeof docUrl === 'string' && docUrl.includes(',')) urls = docUrl.split(',').map(u => u.trim()).filter(u => u);
                            else if (Array.isArray(docUrl)) urls = docUrl;
                            else urls = [docUrl];
                          }
                          const hasUrl = urls.length > 0;
                          return (
                            <button
                              key={`veh-doc-${idx}`}
                              onClick={() => {
                                if (hasUrl) { setSelectedDocument(urls[0]); setSelectedDocumentName(name); setIsDocumentViewerOpen(true); setIsMediaLoading(true); setMediaError(false); } 
                                else { toast.error('No file URL available for this document'); }
                              }}
                              className={`text-[11px] font-medium border rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 transition-all max-w-full truncate cursor-pointer ${hasUrl ? 'text-purple-600 hover:text-purple-800 border-purple-200 hover:border-purple-300 bg-purple-50 hover:bg-purple-100' : 'text-slate-400 border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'}`}
                              disabled={!hasUrl}
                            >
                              <FileText size={12} className={hasUrl ? 'text-purple-500' : 'text-slate-400'} />
                              <span className="truncate max-w-[150px]">{name}</span>
                              {docStatus && docStatus.toLowerCase() !== 'pending' && (
                                <span className={`text-[8px] font-bold ml-1 px-1.5 py-0.5 rounded ${docStatus.toLowerCase() === 'verified' ? 'bg-green-100 text-green-700' : docStatus.toLowerCase() === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{docStatus}</span>
                              )}
                              {hasUrl && urls.length > 1 && <span className="text-[8px] text-purple-400 font-bold ml-0.5">+{urls.length - 1}</span>}
                              {hasUrl && <ExternalLink size={10} className="text-purple-400 flex-shrink-0 ml-1" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {m.role !== 'user' && m.sources && (
  <div className="flex flex-wrap gap-2 px-1">
    {m.sources.map((source, idx) => (
      <button 
        key={idx} 
        onClick={() => {
          if (typeof onNavigateToTarget === 'function') {
            onNavigateToTarget({ 
              tabId: source.tab?.tabId || source.tabId, 
              accordionId: source.accordion?.accordionId || source.accordionId, 
              fieldLabel: source.tab?.fieldLabel || source.accordion?.fieldLabel || source.fieldLabel 
            });
          }
        }} 
        className="text-[11px] font-medium text-slate-500 hover:text-sky-700 focus:outline-none rounded-sm flex items-center gap-1 transition-colors group cursor-pointer"
      >
        <ExternalLink size={10} className="text-slate-400 group-hover:text-sky-500" /> 
        <span className="underline decoration-slate-300 underline-offset-2 group-hover:decoration-sky-300">
          View {source.accordion?.fieldLabel || source.tab?.fieldLabel || source.fieldLabel || 'Details'}
        </span>
      </button>
    ))}
  </div>
)}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex w-full gap-3 justify-start">
            <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5"><Sparkles size={14} className="text-sky-600" /></div>
            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm shadow-sm px-4 py-4 w-fit flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full motion-safe:animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full motion-safe:animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full motion-safe:animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>

      {/* Input & Actions Area */}
      <div className="p-4 pt-0 bg-slate-50/50 relative shrink-0">
        {bookings && (
          <div className="flex items-center gap-2 pb-3 overflow-x-auto no-scrollbar scroll-smooth focus-visible:outline-none rounded-lg" tabIndex={0}>
            {smartSuggestions.map((s, i) => (
              <button key={`alert-${i}`} onClick={() => sendMessage(s.text)} disabled={loading} className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100 flex items-center gap-1 whitespace-nowrap flex-shrink-0 focus:outline-none transition-colors">
                <AlertTriangle size={12} className="text-amber-600" /> {s.text}
              </button>
            ))}
            {smartSuggestions.length > 0 && <div className="w-px h-5 bg-slate-300 mx-1 flex-shrink-0" />}
            {QUICK_ACTIONS.slice(0, 4).map(({ label, icon: Icon, question }) => (
              <button key={label} onClick={() => sendMessage(question)} disabled={loading} className="text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 focus:outline-none transition-colors">
                <Icon size={12} className="text-slate-500" /> {label}
              </button>
            ))}
            <button id="more-actions-btn" onClick={() => setDropdownOpen(!dropdownOpen)} disabled={loading} className="text-[11px] font-medium p-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center justify-center flex-shrink-0 w-8 h-8 focus:outline-none transition-colors">
              <MoreHorizontal size={14} />
            </button>
          </div>
        )}

        {dropdownOpen && bookings && (
          <div ref={dropdownRef} className="absolute bottom-[72px] right-4 mb-2 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
            {QUICK_ACTIONS.slice(4).map(({ label, icon: Icon, question }) => (
              <button key={label} onClick={() => { sendMessage(question); setDropdownOpen(false); }} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2 focus:outline-none transition-colors">
                <Icon size={13} className="text-slate-500" /> {label}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2 relative">
          <input 
            id="chat-input" className="flex-1 text-sm border border-slate-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500 bg-white shadow-sm placeholder:text-slate-400 text-slate-800" 
            value={input} onChange={e => setInput(e.target.value)} 
            onKeyDown={e => { if (e.key === 'Escape') setInput(''); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} 
            placeholder={bookings ? "Ask anything about this booking..." : "No booking selected"} 
            disabled={loading || !bookings} autoComplete="off"
          />
          <button onClick={() => sendMessage()} disabled={loading || !input.trim() || !bookings} className="w-11 h-11 flex items-center justify-center rounded-lg bg-sky-600 text-white shadow-sm hover:bg-sky-700 disabled:opacity-40 focus:outline-none transition-colors">
            <ArrowUp size={18} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}